import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import RecentlyViewed from "../components/RecentlyViewed";
import InteractiveCategoryDesign from "../components/InteractiveCategoryDesign";
import { useCategoryPage } from "../hooks/useCategoryPage";

import { CategorySEO } from "../components/category/CategorySEO";
import { CategoryHeader } from "../components/category/CategoryHeader";
import { SubcategoryPanel, CategoryGrid } from "../components/category/CategorySubcategories";
import { ProductGrid, CategoryBestsellers } from "../components/category/CategoryProducts";
import CategoryMobileFilters from "../components/category/CategoryMobileFilters";
import { FilterBar } from "../components/category/CategoryFilterBar";

function BrandStrip({ brandConfigs, catBrands, toggleBrand }) {
    if (!catBrands || catBrands.length < 2) return null;
    const brandsWithLogos = catBrands
        .map(name => ({ name, logo_url: brandConfigs.find(b => b.name === name)?.logo_url }))
        .filter(b => b.logo_url);
    if (brandsWithLogos.length === 0) return null;

    return (
        <div className="mb-3 bg-white/70 rounded-xl border border-gray-100 px-3 py-2" data-testid="category-brand-strip">
            <div className="flex flex-wrap items-center justify-center gap-2">
                {brandsWithLogos.map(b => (
                    <button key={b.name} onClick={() => toggleBrand(b.name)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-100 hover:border-green-300 hover:shadow-sm transition-all group"
                        data-testid={`brand-strip-${b.name}`}>
                        <img src={b.logo_url} alt={b.name} className="h-6 w-auto object-contain max-w-[60px]" loading="lazy"
                            onError={(e) => { e.target.style.display = 'none'; }} />
                        <span className="text-xs font-medium text-gray-600 group-hover:text-green-700">{b.name}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}

export default function CategoryPage() {
    const ctx = useCategoryPage();

    return (
        <div className="min-h-screen bg-gray-50/50">
            <CategorySEO currentCat={ctx.currentCat} products={ctx.products} total={ctx.total} slug={ctx.slug} searchQ={ctx.searchQ} />
            <Navbar />

            <div className="sg-container pt-4 pb-6">
                <CategoryHeader
                    searchQ={ctx.searchQ} currentCat={ctx.currentCat} loading={ctx.loading}
                    products={ctx.products} total={ctx.total} slug={ctx.slug} breadcrumb={ctx.breadcrumb}
                />

                {!ctx.slug && !ctx.searchQ && <CategoryGrid categories={ctx.categories} />}

                <div className={`transition-all duration-200 ${ctx.transitioning ? "opacity-0 -translate-y-1" : "opacity-100 translate-y-0"}`}>
                    {ctx.isRootCategory && ctx.subcategoryChildren.length > 1 && ctx.categoryDisplayStyle !== "designer" && (
                        <SubcategoryPanel parent={ctx.currentCat} children={ctx.subcategoryChildren} currentSlug={ctx.slug} displayStyle={ctx.categoryDisplayStyle} />
                    )}
                    {ctx.currentCat?.id && ctx.categoryDisplayStyle === "designer" && (
                        <InteractiveCategoryDesign categoryId={ctx.currentCat.id} categories={ctx.categories} />
                    )}
                </div>

                <BrandStrip brandConfigs={ctx.brandConfigs} catBrands={ctx.catBrands} toggleBrand={ctx.toggleBrand} />

                <FilterBar
                    search={ctx.search} setSearch={ctx.setSearch} sort={ctx.sort} setSort={ctx.setSort}
                    priceRange={ctx.priceRange} setPriceRange={ctx.setPriceRange}
                    catBrands={ctx.catBrands} selectedBrands={ctx.selectedBrands} toggleBrand={ctx.toggleBrand}
                    inStockOnly={ctx.inStockOnly} setInStockOnly={ctx.setInStockOnly}
                    onSaleOnly={ctx.onSaleOnly} setOnSaleOnly={ctx.setOnSaleOnly}
                    paramOptions={ctx.paramOptions} selectedParams={ctx.selectedParams} toggleParam={ctx.toggleParam}
                    activeParamCount={ctx.activeParamCount} hasFilters={ctx.hasFilters} clearFilters={ctx.clearFilters}
                    viewMode={ctx.viewMode} handleViewModeChange={ctx.handleViewModeChange}
                    pageLimit={ctx.pageLimit} setPageLimit={ctx.setPageLimit} setPage={ctx.setPage}
                    setFiltersOpen={ctx.setFiltersOpen}
                    isLoggedIn={ctx.isLoggedIn} savedFilters={ctx.savedFilters}
                    showSaveFilter={ctx.showSaveFilter} setShowSaveFilter={ctx.setShowSaveFilter}
                    saveFilterName={ctx.saveFilterName} setSaveFilterName={ctx.setSaveFilterName}
                    handleSaveFilter={ctx.handleSaveFilter} handleLoadFilter={ctx.handleLoadFilter} handleDeleteFilter={ctx.handleDeleteFilter}
                />

                <CategoryBestsellers bestsellers={ctx.bestsellers} currentCat={ctx.currentCat} hasFilters={ctx.hasFilters} searchQ={ctx.searchQ} slug={ctx.slug} />

                <ProductGrid
                    products={ctx.products} viewMode={ctx.viewMode}
                    promoCards={ctx.promoCards} promoFrequency={ctx.promoFrequency}
                    pageLimit={ctx.pageLimit} loading={ctx.loading} total={ctx.total}
                    hasMore={ctx.hasMore} loadingMore={ctx.loadingMore}
                    hasFilters={ctx.hasFilters} onLoadMore={ctx.handleLoadMore} onClearFilters={ctx.clearFilters}
                    transitioning={ctx.transitioning}
                />
            </div>

            <CategoryMobileFilters
                filtersOpen={ctx.filtersOpen} setFiltersOpen={ctx.setFiltersOpen}
                catBrands={ctx.catBrands} selectedBrands={ctx.selectedBrands} toggleBrand={ctx.toggleBrand}
                paramOptions={ctx.paramOptions} selectedParams={ctx.selectedParams} toggleParam={ctx.toggleParam}
                inStockOnly={ctx.inStockOnly} setInStockOnly={ctx.setInStockOnly}
                onSaleOnly={ctx.onSaleOnly} setOnSaleOnly={ctx.setOnSaleOnly}
                priceRange={ctx.priceRange} setPriceRange={ctx.setPriceRange}
                hasFilters={ctx.hasFilters} clearFilters={ctx.clearFilters}
            />

            <div className="sg-container mb-8">
                <RecentlyViewed maxShow={6} />
            </div>

            <Footer />
        </div>
    );
}
